import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/types/api';
import { setupInterceptors, notifyUnconfiguredRequestError } from './interceptors';
import { API_CONFIG } from '@/config/env';
import { nanoid } from 'nanoid';
import { hashString } from '@/utils/string';
import type { UserErrorContext } from '@/utils/userError';

/**
 * 自定义请求配置
 */
export interface IRequestConfig extends AxiosRequestConfig {
  /** 页面接管局部错误时关闭默认 Toast，不改变失败、取消或认证处理 */
  showErrorToast?: boolean;
  /** 仅用于错误文案兜底，不改变请求及重试行为 */
  errorContext?: UserErrorContext;
  /**
   * 当allowCancel为true时，请求开始时会调用该方法
   * @param cancelRequest 取消请求的方法
   * @param requestKey 请求唯一标识
   */
  startRequest?: (cancelRequest: () => void, requestKey: string) => void;
  /**
   * 是否允许取消请求，默认为 true
   * 开启后会为该请求添加取消令牌，页面切换时会自动取消未完成的请求，
   * 并且支持从startRequest中获取手动取消请求的方法
   */
  allowCancel?: boolean;
}

/**
 * 创建 Axios 实例
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
    'Client-Id': 'biz_pc_v1',
    'Client-Version': '1.0.0',
  },
});

// 设置拦截器
setupInterceptors(apiClient);

/**
 * 请求取消管理器
 */
class RequestCancelManager {
  private pendingRequests: Map<string, AbortController> = new Map();

  /**
   * 生成请求的唯一标识
   */
  public generateRequestKey(config: AxiosRequestConfig): string {
    const { url, method, params, data } = config;
    // 进行哈希处理，避免RequestKey过长
    const hash = hashString(`${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`);
    return `${method}:${hash}&_id=${nanoid()}`;
  }

  getPendingRequestsSize() {
    return this.pendingRequests.size;
  }

  /**
   * 添加请求
   */
  addRequest(requestKey: string): AbortController {
    // 获取不包含id的基础 key
    const baseKey = requestKey.substring(0, requestKey.lastIndexOf('&_id='));

    // 如果已存在相同请求(忽略id),取消之前的请求
    this.pendingRequests.forEach((_, key) => {
      if (key.startsWith(baseKey + '&_id=')) {
        this.cancelRequest(key);
      }
    });

    const controller = new AbortController();
    this.pendingRequests.set(requestKey, controller);

    return controller;
  }

  /**
   * 移除请求
   */
  removeRequest(config: AxiosRequestConfig): void {
    const rk = config.headers?.['Request-Key'] as string;
    if (rk) {
      this.pendingRequests.delete(rk);
    }
  }

  /**
   * 取消指定请求
   */
  cancelRequest(requestKey: string): void {
    const controller = this.pendingRequests.get(requestKey);
    if (controller) {
      controller.abort();
      this.pendingRequests.delete(requestKey);
    }
  }

  /**
   * 取消所有请求
   */
  cancelAllRequests(): void {
    this.pendingRequests.forEach((controller) => {
      controller.abort();
    });
    console.log(`[cancelAllRequests]已取消${this.pendingRequests.size}个请求`);
    this.pendingRequests.clear();
  }
}

// 导出请求取消管理器实例
export const cancelManager = new RequestCancelManager();

/**
 * 处理请求配置，添加取消功能
 */
const handleRequestConfig = (
  url: string,
  method: string,
  config: IRequestConfig = {}
): AxiosRequestConfig => {
  const { allowCancel = true, startRequest, ...restConfig } = config;

  if (!allowCancel) {
    return restConfig;
  }

  const fullConfig: AxiosRequestConfig = { ...restConfig, url, method };
  const requestKey = cancelManager.generateRequestKey(fullConfig);
  const controller = cancelManager.addRequest(requestKey);

  restConfig.headers = {
    ...restConfig.headers,
    'Request-Key': requestKey,
  };

  if (startRequest) {
    startRequest(() => cancelManager.cancelRequest(requestKey), requestKey);
  }

  return {
    ...restConfig,
    signal: controller.signal,
  };
};

export const CANCEL_ERR_CODE = 40000;
const CANCEL_PAYLOAD: ApiResponse = {
  code: CANCEL_ERR_CODE,
  msg: '请求已取消',
};

/**
 * 通用请求方法
 */
export const request = {
  get: async <T = unknown>(url: string, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'get', config);
    try {
      const res = await apiClient.get<ApiResponse<T>>(url, finalConfig);
      return res.data;
    } catch (error) {
      notifyUnconfiguredRequestError(error, config);
      if (error instanceof Error && error.message === 'canceled') {
        return CANCEL_PAYLOAD as ApiResponse<T>;
      }
      throw error;
    }
  },
  getRaw: <T = unknown>(url: string, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'get', config);
    return apiClient.get<ApiResponse<T>>(url, finalConfig).catch((error: unknown) => {
      notifyUnconfiguredRequestError(error, config);
      throw error;
    });
  },
  post: async <T = unknown>(url: string, data?: unknown, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'post', { ...config, data });
    try {
      const res = await apiClient.post<ApiResponse<T>>(url, data, finalConfig);
      return res.data;
    } catch (error) {
      notifyUnconfiguredRequestError(error, config);
      if (error instanceof Error && error.message === 'canceled') {
        return CANCEL_PAYLOAD as ApiResponse<T>;
      }
      throw error;
    }
  },
  postRaw: <T = unknown>(url: string, data?: unknown, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'post', { ...config, data });
    return apiClient.post<ApiResponse<T>>(url, data, finalConfig).catch((error: unknown) => {
      notifyUnconfiguredRequestError(error, config);
      throw error;
    });
  },
  put: async <T = unknown>(url: string, data?: unknown, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'put', { ...config, data });
    try {
      const res = await apiClient.put<ApiResponse<T>>(url, data, finalConfig);
      return res.data;
    } catch (error) {
      notifyUnconfiguredRequestError(error, config);
      if (error instanceof Error && error.message === 'canceled') {
        return CANCEL_PAYLOAD as ApiResponse<T>;
      }
      throw error;
    }
  },
  putRaw: <T = unknown>(url: string, data?: unknown, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'put', { ...config, data });
    return apiClient.put<ApiResponse<T>>(url, data, finalConfig).catch((error: unknown) => {
      notifyUnconfiguredRequestError(error, config);
      throw error;
    });
  },
  delete: async <T = unknown>(url: string, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'delete', config);
    try {
      const res = await apiClient.delete<ApiResponse<T>>(url, finalConfig);
      return res.data;
    } catch (error) {
      notifyUnconfiguredRequestError(error, config);
      if (error instanceof Error && error.message === 'canceled') {
        return CANCEL_PAYLOAD as ApiResponse<T>;
      }
      throw error;
    }
  },
  deleteRaw: <T = unknown>(url: string, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'delete', config);
    return apiClient.delete<ApiResponse<T>>(url, finalConfig).catch((error: unknown) => {
      notifyUnconfiguredRequestError(error, config);
      throw error;
    });
  },
  patch: async <T = unknown>(url: string, data?: unknown, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'patch', { ...config, data });
    try {
      const res = await apiClient.patch<ApiResponse<T>>(url, data, finalConfig);
      return res.data;
    } catch (error) {
      notifyUnconfiguredRequestError(error, config);
      if (error instanceof Error && error.message === 'canceled') {
        return CANCEL_PAYLOAD as ApiResponse<T>;
      }
      throw error;
    }
  },
  patchRaw: <T = unknown>(url: string, data?: unknown, config?: IRequestConfig) => {
    const finalConfig = handleRequestConfig(url, 'patch', { ...config, data });
    return apiClient.patch<ApiResponse<T>>(url, data, finalConfig).catch((error: unknown) => {
      notifyUnconfiguredRequestError(error, config);
      throw error;
    });
  },
};

export default apiClient;
